import { useState } from 'react';
import { Shield, Key, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { activateLicense } from '@/lib/tauri';
import { useLicenseStore } from '@/stores/licenseStore';
import { toast } from 'sonner';

interface DesktopOnboardingProps {
  onComplete: () => void;
}

/**
 * First-time desktop onboarding flow
 * - Enter license key
 * - Activate with device fingerprint
 * - Store license locally
 */
export function DesktopOnboarding({ onComplete }: DesktopOnboardingProps) {
  const [licenseKey, setLicenseKey] = useState('');
  const [isActivating, setIsActivating] = useState(false);
  const [error, setError] = useState<string>();

  const { setLicense } = useLicenseStore();

  const handleActivate = async () => {
    if (!licenseKey.trim()) {
      setError('Veuillez entrer votre clé de licence');
      return;
    }

    // Validate format: LEBIZ-XXXX-XXXX-XXXX-XXXX
    const licensePattern = /^LEBIZ-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/;
    if (!licensePattern.test(licenseKey.toUpperCase())) {
      setError('Format de clé invalide. Format attendu: LEBIZ-XXXX-XXXX-XXXX-XXXX');
      return;
    }

    setIsActivating(true);
    setError(undefined);

    try {
      console.log('🔐 Activating license:', licenseKey);

      // Call Tauri to activate license
      const licenseInfo = await activateLicense(licenseKey.toUpperCase());

      console.log('✅ License activated successfully');

      // Store in Zustand
      setLicense(licenseInfo);

      toast.success('✅ Licence activée avec succès');

      // Complete onboarding
      setTimeout(() => {
        onComplete();
      }, 1000);
    } catch (err: any) {
      console.error('❌ Activation failed:', err);
      setError(err.message || 'Échec de l\'activation. Veuillez réessayer.');
      toast.error('Échec de l\'activation de la licence');
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-emerald-100 rounded-full">
              <Shield className="w-12 h-12 text-emerald-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bienvenue sur leBusiness
          </h1>
          <p className="text-gray-600">
            Activez votre licence pour commencer
          </p>
          <p className="text-sm text-gray-500 mt-1 text-right">
            مرحبا بكم في leBusiness
          </p>
        </div>

        {/* License input form */}
        <div className="bg-white border-2 border-gray-200 rounded-lg p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="license-key" className="text-sm font-medium">
              Clé de licence / مفتاح الترخيص
            </Label>
            <div className="relative">
              <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="license-key"
                placeholder="LEBIZ-XXXX-XXXX-XXXX-XXXX"
                value={licenseKey}
                onChange={(e) => {
                  setLicenseKey(e.target.value.toUpperCase());
                  setError(undefined);
                }}
                className="pl-10 font-mono text-sm"
                maxLength={29}
                disabled={isActivating}
              />
            </div>
            <p className="text-xs text-gray-500">
              Format: LEBIZ-XXXX-XXXX-XXXX-XXXX
            </p>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleActivate}
            disabled={isActivating || !licenseKey}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            size="lg"
          >
            {isActivating ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Activation en cours...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Activer la licence
              </>
            )}
          </Button>
        </div>

        {/* Info section */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-medium text-blue-900 mb-2">
            📝 Informations importantes
          </h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>La licence sera liée à cet appareil</li>
            <li>Vérification mensuelle requise (même hors ligne)</li>
            <li>Période de grâce de 7 jours après vérification</li>
            <li>Toutes les données sont stockées localement</li>
          </ul>
          <p className="text-xs text-blue-700 mt-3 text-right leading-relaxed">
            سيتم ربط الترخيص بهذا الجهاز • التحقق الشهري مطلوب • فترة سماح 7 أيام
          </p>
        </div>

        {/* Support */}
        <div className="text-center text-sm text-gray-600">
          <p>Besoin d'aide ?</p>
          <a
            href="mailto:support@lebusiness.dz"
            className="text-emerald-600 hover:underline"
          >
            support@lebusiness.dz
          </a>
        </div>
      </div>
    </div>
  );
}
